import { useEffect, useRef, useState } from 'react';

// ─── Types ────────────────────────────────────────────────────────────────────

export type TrackerData = {
  id: number;
  device_id: string;
  name: string;
  location_name: string;
  latitude: number | null;
  longitude: number | null;
  tracker_type: string;
  node_role: 'standalone' | 'master' | 'slave';
  cluster_id: number | null;
  cluster_name: string | null;
  command_transport: string;
  capabilities: Record<string, unknown>;
};

export type TrackerTelemetry = {
  // Angles (primary field from firmware)
  actual_tilt_x?: number;    // primary axis angle (degrees)
  actual_tilt_y?: number;    // secondary axis angle (dual-axis trackers)
  target_tilt_x?: number;
  target_tilt_y?: number;
  actual_angle?: number;     // legacy fallback

  // Mode / Status
  operating_mode?: string;   // 'sun_tracking' | 'manual' | 'park' | 'wind_stow' | 'safe_mode'
  health_status?: string;    // 'optimal' | 'warning' | 'critical'
  mode?: string;             // 'AUTO' | 'MANUAL'
  auto?: string;             // 'SUN' | 'GPS' | 'LIGHT'

  // Environment
  wind_speed?: number;       // m/s
  temperature?: number;      // °C
  sun_azimuth?: number;
  sun_elevation?: number;
  light_east?: number;
  light_west?: number;
  light_top?: number;
  light_bottom?: number;

  // Power / Energy
  power_w?: number;
  output_w?: number;
  current_a?: number;
  voltage?: number;
  energy_kwh_interval?: number;  // 5-min bucket

  [key: string]: unknown;
};

export type EnergyHistoryPoint = {
  bucket_start: string;
  energy_kwh: number;
};

export type EnergyHistory = {
  range: string;
  total_kwh: number;
  points: EnergyHistoryPoint[];
};

export type TrackerApiState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'site_ready'; apiBase: string; token: string }
  | { status: 'error'; message: string }
  | {
      status: 'ready';
      trackers: TrackerData[];          // all user trackers
      primaryTracker: TrackerData;      // first / main tracker (for lat/lon)
      apiBase: string;
      token: string;
    };

export type LiveMode = 'simulation' | 'live';

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Reads ?token=&api_base= from URL.
 * Loads ALL user trackers, opens a WebSocket for live telemetry,
 * and exposes sendCommand / sendBulkCommand helpers.
 *
 * onTelemetry(device_id, telemetry) is called on every WS push.
 */
export function useTrackerApi(
  onTelemetry: (deviceId: string, t: TrackerTelemetry) => void
) {
  const [state, setState] = useState<TrackerApiState>({ status: 'idle' });
  const [liveMode, setLiveMode] = useState<LiveMode>('simulation');
  const [telemetryMap, setTelemetryMap] = useState<Record<string, TrackerTelemetry>>({});
  const [energyHistory, setEnergyHistory] = useState<Record<string, EnergyHistory>>({});

  const wsRef = useRef<WebSocket | null>(null);
  const onTelemetryRef = useRef(onTelemetry);
  onTelemetryRef.current = onTelemetry;

  // ── Helper: build proxy URL (avoids mixed-content + CSP issues) ───────────
  // All API calls go through /api/twin?api_base=...&path=... on the same origin.
  const proxyUrl = (apiBase: string, path: string) =>
    `/api/twin?api_base=${encodeURIComponent(apiBase)}&path=${encodeURIComponent(path)}`;

  // ── Init: read URL params, fetch trackers ──────────────────────────────────
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.has('project')) return; // Project planning never connects to device control.
    const token   = params.get('token');
    const apiBase = (params.get('api_base') ?? '').replace(/\/$/, '');

    if (!token || !apiBase) {
      setState({ status: 'idle' });
      return;
    }

    setState({ status: 'loading' });

    fetch(proxyUrl(apiBase, '/api/trackers'), {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => {
        if (r.status === 401) throw new Error('Token abgelaufen oder ungültig');
        if (!r.ok) throw new Error(`Tracker-API Fehler: ${r.status}`);
        return r.json() as Promise<TrackerData[]>;
      })
      .then((trackers) => {
        if (!trackers.length) {
          if (params.get('site')) {
            // A consultation precedes hardware installation. Keep live controls
            // unavailable, but allow the separately authorised site model load.
            setState({ status: 'site_ready', apiBase, token });
            return;
          }
          throw new Error('Keine Tracker für diesen User gefunden');
        }
        setState({
          status: 'ready',
          trackers,
          primaryTracker: trackers[0],
          apiBase,
          token,
        });
      })
      .catch((e: Error) => setState({ status: 'error', message: e.message }));
  }, []);

  // ── WebSocket (wss:// only) or polling fallback for http user-apps ──────────
  useEffect(() => {
    if (state.status !== 'ready') return;
    const { apiBase, token } = state;

    // Polling fallback: fetch /api/trackers every 5s via proxy
    const startPolling = () => {
      let alive = true;
      const poll = async () => {
        if (!alive) return;
        try {
          const r = await fetch(proxyUrl(apiBase, '/api/trackers'), {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!r.ok) return;
          const trackers = await r.json() as Array<{ device_id: string; [k: string]: unknown }>;
          // trackers endpoint doesn't return live telemetry, but we can read any cached state
          // Telemetry polling: fetch per-tracker status
          for (const tr of trackers) {
            try {
              const tr2 = await fetch(
                proxyUrl(apiBase, `/api/trackers/${tr.device_id}/history?range=day`),
                { headers: { Authorization: `Bearer ${token}` } }
              );
              if (tr2.ok) {
                const data = await tr2.json() as EnergyHistory;
                setEnergyHistory((prev) => ({ ...prev, [tr.device_id]: data }));
              }
            } catch {}
          }
        } catch {}
        if (alive) setTimeout(poll, 5000);
      };
      poll();
      return () => { alive = false; };
    };

    // If api_base is http → browser blocks ws:// from an https page → poll instead.
    //
    // The https case is NOT automatically safe either: this document's own CSP
    // gates WebSocket handshakes through connect-src, and api_base points at a
    // third-party origin (the tracker backend), so that origin must be listed in
    // the connectSrc directive in src/Server.mjs. When it is not, the WebSocket
    // CONSTRUCTOR THROWS SYNCHRONOUSLY — Firefox NS_ERROR_CONTENT_BLOCKED,
    // Chrome SecurityError — and an uncaught throw inside this effect tears down
    // the whole React tree, leaving a completely black page with no scene and no
    // splash. That regression shipped the moment api_base flipped from http to
    // https and this branch ran in production for the first time. Never let the
    // constructor throw escape: degrade to polling, which still delivers energy
    // history, just without live push.
    if (apiBase.startsWith('https://')) {
      try {
        const wsBase = apiBase.replace(/^https/, 'wss');
        const ws = new WebSocket(`${wsBase}/ws/telemetry?token=${encodeURIComponent(token)}`);
        wsRef.current = ws;

        ws.onmessage = (evt) => {
          try {
            const msg = JSON.parse(evt.data as string);
            if (msg.type === 'state' && msg.data) {
              const snap = msg.data as Record<string, TrackerTelemetry>;
              setTelemetryMap(snap);
              Object.entries(snap).forEach(([id, t]) => onTelemetryRef.current(id, t));
            } else if (msg.type === 'tracker.telemetry') {
              const deviceId: string = msg.device_id;
              const t: TrackerTelemetry = msg.tracker ?? msg.data ?? {};
              setTelemetryMap((prev) => ({ ...prev, [deviceId]: t }));
              onTelemetryRef.current(deviceId, t);
            }
          } catch {}
        };
        ws.onerror = () => console.warn('[TrackerApi] WebSocket error');
        return () => { ws.close(); wsRef.current = null; };
      } catch (err) {
        console.warn('[TrackerApi] WebSocket blocked (check connect-src for this api_base) — falling back to polling:', err);
      }
    }

    return startPolling();
  }, [state.status]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Fetch daily energy history for all trackers when switching to Live ─────
  useEffect(() => {
    if (liveMode !== 'live' || state.status !== 'ready') return;
    const { trackers, apiBase, token } = state;

    trackers.forEach(async (tracker) => {
      try {
        const r = await fetch(
          proxyUrl(apiBase, `/api/trackers/${tracker.device_id}/history?range=day`),
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!r.ok) return;
        const data = await r.json() as EnergyHistory;
        setEnergyHistory((prev) => ({ ...prev, [tracker.device_id]: data }));
      } catch {}
    });
  }, [liveMode, state.status]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Commands ───────────────────────────────────────────────────────────────

  async function sendCommand(deviceId: string, mode: string, extra?: Record<string, unknown>) {
    if (state.status !== 'ready') return;
    const { apiBase, token } = state;
    await fetch(proxyUrl(apiBase, `/api/trackers/${deviceId}/command`), {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ mode, ...extra }),
    });
  }

  async function sendBulkCommand(mode: string, extra?: Record<string, unknown>) {
    if (state.status !== 'ready') return;
    const { trackers, apiBase, token } = state;
    const device_ids = trackers.map((t) => t.device_id);
    await fetch(proxyUrl(apiBase, '/api/trackers/bulk-command'), {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ device_ids, params: { mode, ...extra } }),
    });
  }

  return {
    state,
    liveMode,
    setLiveMode,
    telemetryMap,
    energyHistory,
    sendCommand,
    sendBulkCommand,
  };
}

// ─── Helper: extract tilt angle from telemetry ────────────────────────────────
export function getTiltAngle(t: TrackerTelemetry): number | undefined {
  return t.actual_tilt_x ?? t.actual_angle ?? undefined;
}
